/**
 * RangeHourPicker 纯函数层：小时 slot 对齐、范围 clamp、日历辅助逻辑。
 * 业务约束：可选时刻 01:00–23:59，最长含首尾 N 个日历日（默认 7 天）。
 */
import moment, { Moment } from 'moment';
import {
  isAfter,
  isBefore,
  isSame,
} from 'tea-component/es/datepicker/util';
import type { DateShortcutType } from '@/pages/home/components/RangePicker/_config';
import type { IHourRangeTranslation, IHourSlot, THourRangeValue } from './types';

/** 01:00–23:00 整点 + 23:59（日末）；不含 00:00 */
export const DEFAULT_HOUR_SLOTS: IHourSlot[] = (() => {
  const slots: IHourSlot[] = [];
  for (let hour = 1; hour <= 23; hour++) {
    const label = `${String(hour).padStart(2, '0')}:00`;
    slots.push({ label, hour, minute: 0 });
  }
  slots.push({ label: '23:59', hour: 23, minute: 59 });
  return slots;
})();

export const DEFAULT_MAX_HOUR_RANGE_DAYS = 7;

export const HOUR_RANGE_DISPLAY_FORMAT = 'YYYY-MM-DD HH:mm';

export const DEFAULT_HOUR_RANGE_VALUE: ['', ''] = ['', ''];

export const DEFAULT_HOUR_TRANSLATION: IHourRangeTranslation = {
  selectDate: 'Select Date',
  selectHour: 'Select Hour',
  okText: 'Confirm',
};

export const HOUR_TRANSLATION_CN: IHourRangeTranslation = {
  selectDate: '选择日期',
  selectHour: '选择小时',
  okText: '确认',
};

type THourBoundary = 'start' | 'end';
type TRangeBound = [Moment | null, Moment | null];

/** 含首尾计数：07-01 ~ 07-07 = 7 天 */
export const countInclusiveCalendarDays = (
  start: Moment,
  end: Moment,
): number =>
  end.clone().startOf('day').diff(start.clone().startOf('day'), 'days') + 1;

export const getMaxSelectableEndDay = (
  start: Moment,
  maxRangeDays: number,
): Moment =>
  start.clone().startOf('day').add(maxRangeDays - 1, 'days');

export const isWithinMaxHourRangeDays = (
  start: Moment,
  end: Moment,
  maxRangeDays: number,
): boolean =>
  countInclusiveCalendarDays(start, end) <= maxRangeDays;

/** tea DateTable：true = 可选。有 start 锚点时限制 end 在 maxRangeDays 个含首尾日历日内 */
export const createDefaultDisabledDate =
  (maxRangeDays: number) =>
    (date: moment.Moment, startDate?: moment.Moment): boolean => {
      if (!date?.isValid()) return false;

      if (startDate?.isValid()) {
        const minDate = moment(startDate).startOf('day');
        const maxDate = getMaxSelectableEndDay(startDate, maxRangeDays).endOf(
          'day',
        );
        return (
          !date.isBefore(minDate, 'day') && !date.isAfter(maxDate, 'day')
        );
      }

      return true;
    };

/** 00:00 分别映射到最早/最晚可选时刻（01:00、23:59） */
const clampHourMoment = (value: Moment, boundary: THourBoundary): Moment => {
  const normalized = value.clone().milliseconds(0);
  if (boundary === 'start') {
    return normalized.hour() === 0
      ? normalized.hour(1).minute(0).second(0)
      : normalized.second(0);
  }
  return normalized.hour() === 0
    ? normalized.hour(23).minute(59).second(0)
    : normalized.second(0);
};

export const findHourSlotIndex = (
  value: Moment,
  slots: IHourSlot[] = DEFAULT_HOUR_SLOTS,
): number => {
  if (!value?.isValid()) return -1;
  const hour = value.hour();
  const minute = value.minute();
  return slots.findIndex(
    (slot) => slot.hour === hour && slot.minute === minute,
  );
};

/**
 * 向下取整到最近可选 slot（含跨日：当日 01:00 前回退到前一日 23:59）。
 * 用于快捷选项：避免 clampHourMoment 将 00:xx 映射到「当日」01:00/23:59。
 */
export const floorMomentToHourSlot = (
  value: Moment,
  slots: IHourSlot[] = DEFAULT_HOUR_SLOTS,
): Moment => {
  if (!value?.isValid()) return value;

  const dayMinutes = value.hour() * 60 + value.minute();
  const firstSlotMinutes = slots[0].hour * 60 + slots[0].minute;

  if (dayMinutes < firstSlotMinutes) {
    const lastSlot = slots[slots.length - 1];
    return value
      .clone()
      .subtract(1, 'day')
      .hour(lastSlot.hour)
      .minute(lastSlot.minute)
      .second(0)
      .millisecond(0);
  }

  let matched = slots[0];
  for (const slot of slots) {
    const slotMinutes = slot.hour * 60 + slot.minute;
    if (slotMinutes <= dayMinutes) {
      matched = slot;
    } else {
      break;
    }
  }

  return value
    .clone()
    .hour(matched.hour)
    .minute(matched.minute)
    .second(0)
    .millisecond(0);
};

/** 精确匹配 slot；否则 start 取首个 slot、end 取末个 slot */
export const snapMomentToHourSlot = (
  value: Moment,
  boundary: THourBoundary = 'start',
  slots: IHourSlot[] = DEFAULT_HOUR_SLOTS,
): Moment => {
  if (!value?.isValid()) return value;

  const index = findHourSlotIndex(value, slots);
  if (index >= 0) {
    const slot = slots[index];
    return value
      .clone()
      .hour(slot.hour)
      .minute(slot.minute)
      .second(0)
      .millisecond(0);
  }

  const fallback =
    boundary === 'end' ? slots[slots.length - 1] : slots[0];
  return value
    .clone()
    .hour(fallback.hour)
    .minute(fallback.minute)
    .second(0)
    .millisecond(0);
};

export const clampHourRange = (
  start: Moment | string,
  end: Moment | string,
  maxRangeDays: number = DEFAULT_MAX_HOUR_RANGE_DAYS,
  slots: IHourSlot[] = DEFAULT_HOUR_SLOTS,
): [Moment, Moment] => {
  const startMoment = moment.isMoment(start) ? start.clone() : moment(start);
  const endMoment = moment.isMoment(end) ? end.clone() : moment(end);

  if (!startMoment.isValid() || !endMoment.isValid()) {
    return [startMoment, endMoment];
  }

  const clampedStart = snapMomentToHourSlot(
    clampHourMoment(startMoment, 'start'),
    'start',
    slots,
  );
  let clampedEnd = snapMomentToHourSlot(
    clampHourMoment(endMoment, 'end'),
    'end',
    slots,
  );

  if (clampedEnd.isBefore(clampedStart)) {
    clampedEnd = clampedStart.clone();
  }

  if (!isWithinMaxHourRangeDays(clampedStart, clampedEnd, maxRangeDays)) {
    const maxDay = getMaxSelectableEndDay(clampedStart, maxRangeDays);
    clampedEnd = snapMomentToHourSlot(
      maxDay.clone().hour(23).minute(59).second(0),
      'end',
      slots,
    );
  }

  return [clampedStart, clampedEnd];
};

/** API 用 YYYY-MM-DD HH:mm:ss；boundary 影响非 slot 时间的 snap 方向 */
export const formatHourDateTime = (
  value: moment.MomentInput,
  boundary: THourBoundary = 'start',
  slots: IHourSlot[] = DEFAULT_HOUR_SLOTS,
): string => {
  if (
    value === '' ||
    value === null ||
    value === undefined ||
    !moment(value).isValid()
  ) {
    return '';
  }
  return snapMomentToHourSlot(moment(value), boundary, slots).format(
    'YYYY-MM-DD HH:mm:ss',
  );
};

export const normalizeHourRangeValue = (
  range: THourRangeValue,
  maxRangeDays: number = DEFAULT_MAX_HOUR_RANGE_DAYS,
  slots: IHourSlot[] = DEFAULT_HOUR_SLOTS,
): THourRangeValue => {
  const [start, end] = range;
  if (
    !start ||
    !end ||
    !moment.isMoment(start) ||
    !moment.isMoment(end) ||
    !start.isValid() ||
    !end.isValid()
  ) {
    return range;
  }
  return clampHourRange(start, end, maxRangeDays, slots);
};

export const isValidHourRangeValue = (
  value: THourRangeValue,
): value is [Moment, Moment] =>
  Array.isArray(value) &&
  value.length === 2 &&
  moment.isMoment(value[0]) &&
  moment.isMoment(value[1]) &&
  value[0].isValid() &&
  value[1].isValid();

/** 手动选范围时反查匹配的 shortcut 名称，用于左侧高亮同步 */
export const findMatchingShortcutName = (
  shortcuts: DateShortcutType[],
  value: THourRangeValue,
): string => {
  if (!isValidHourRangeValue(value)) return '';
  const [start, end] = value;
  const matched = shortcuts.find((item) => {
    const range = item.onClick() as THourRangeValue;
    if (!isValidHourRangeValue(range)) return false;
    return (
      range[0].isSame(start, 'minute') && range[1].isSame(end, 'minute')
    );
  });
  return matched?.name ?? '';
};

export const applyHourSlotToMoment = (
  base: Moment,
  slot: IHourSlot,
): Moment =>
  base
    .clone()
    .hour(slot.hour)
    .minute(slot.minute)
    .second(0)
    .millisecond(0);

/**
 * 双月历初始视图。起止同月时左右面板相邻展示；
 * 仅 [start, end] 完整时才跟值走，避免选 end 过程中视图乱跳。
 */
export const getInitialCalendarViews = (
  value: THourRangeValue,
): { start: Moment; end: Moment; } => {
  const parentUnit: moment.unitOfTime.StartOf = 'month';

  if (isValidHourRangeValue(value)) {
    const [start, end] = value;
    if (isSame(start, end, parentUnit)) {
      return {
        start: moment(end).subtract(1, parentUnit),
        end: end.clone(),
      };
    }
    return { start: start.clone(), end: end.clone() };
  }

  const m = moment();
  return {
    start: moment(m).subtract(1, parentUnit),
    end: m.clone(),
  };
};

/** 按当前左右面板月份，切分可选 range 边界，保证双面板不重叠 */
export const getHourRangeCalendarRange = (
  range: TRangeBound | undefined,
  curStartView: Moment,
  curEndView: Moment,
): [TRangeBound, TRangeBound] => {
  const [rangeMin, rangeMax] = range || [null, null];
  const parentUnit: moment.unitOfTime.StartOf = 'month';
  const endPreEnd = moment(curEndView)
    .subtract(1, parentUnit)
    .endOf(parentUnit);
  const startNxtStart = moment(curStartView)
    .add(1, parentUnit)
    .startOf(parentUnit);

  return [
    [rangeMin, rangeMax && isAfter(endPreEnd, rangeMax) ? rangeMax : endPreEnd],
    [
      rangeMin && isBefore(startNxtStart, rangeMin) ? rangeMin : startNxtStart,
      rangeMax,
    ],
  ];
};

/**
 * 构建快捷选项时间范围。
 * end = floor(now - 1h)；start = floor(end - (hours - 1)h)，共 hours 个整点 slot。
 * 相对 end 回推，避免 now/start/end 各自 clamp 在跨午夜时漂移。
 */
export const buildHourShortcutRange = (
  hours: number,
  maxRangeDays: number = DEFAULT_MAX_HOUR_RANGE_DAYS,
  now: Moment = moment(),
  slots: IHourSlot[] = DEFAULT_HOUR_SLOTS,
): [Moment, Moment] => {
  const rawEnd = now.clone().subtract(1, 'hour').second(0).millisecond(0);
  const end = floorMomentToHourSlot(rawEnd, slots);
  const rawStart = end.clone().subtract(hours - 1, 'hours');
  const start = floorMomentToHourSlot(rawStart, slots);
  return clampHourRange(start, end, maxRangeDays, slots);
};

/** 快捷选项 end 取当前时刻前 1 小时（与数据可用性对齐），再经 clampHourRange 裁切 */
export const createHourShortcuts = (
  maxRangeDays: number = DEFAULT_MAX_HOUR_RANGE_DAYS,
): DateShortcutType[] => {
  const buildRange = (hours: number): [Moment, Moment] =>
    buildHourShortcutRange(hours, maxRangeDays);

  return [
    { name: 'Last 1 hour', nameCN: '最近1小时', onClick: () => buildRange(1) },
    { name: 'Last 6 hour', nameCN: '最近6小时', onClick: () => buildRange(6) },
    {
      name: 'Last 12 hour',
      nameCN: '最近12小时',
      onClick: () => buildRange(12),
    },
    {
      name: 'Last 24 hour',
      nameCN: '最近24小时',
      onClick: () => buildRange(24),
    },
  ];
};
